import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Fancybox } from "@fancyapps/ui";
import "@fancyapps/ui/dist/fancybox/fancybox.css";
import moment from "moment";
import "moment/locale/ko";
import { emptyCheck, formatDateTime, maskMiddleCharacters } from "util/function/function";
import { getMatchingOne } from "firebase_/firebase_function/matching";
import { getApplicantsApi, rejectApplyApi, selectApplyApi } from "api/match";
import { useUser } from "util/context/user_context";
import ConfirmDialog from "components/confirm_dialog";
import OnlyConfirmDialog from "components/onlyConfirm_dialog";
import OnlyLoadingPage from "components/loading_page";
import DetailPageButton from "components/repair/detail_page_button";
import {
  faChevronLeft,
  faClipboardCheck,
  faHandshake,
  faFlagCheckered,
  faMicrophone,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const C = {
  green: "#059669",
  greenDark: "#047857",
  greenBg: "#ECFDF5",
  greenBorder: "#A7F3D0",
  ink: "#111827",
  body: "#374151",
  sub: "#6B7280",
  line: "#E5E7EB",
  bg: "#F7FAF7",
  amber100: "#FEF3C7",
  amber800: "#92400E",
};

const STATUS_MAP = {
  0: { label: "매칭중", bg: C.amber100, fg: C.amber800, dot: "#F59E0B" },
  1: { label: "매칭완료", bg: "#DBEAFE", fg: "#1E40AF", dot: "#3B82F6" },
  2: { label: "완료", bg: C.greenBg, fg: C.greenDark, dot: C.green },
};

function StatusChip({ status }) {
  const s = STATUS_MAP[status] ?? STATUS_MAP[0];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 14px",
        borderRadius: 9999,
        background: s.bg,
        color: s.fg,
        fontSize: 13,
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: 9999, background: s.dot }} />
      {s.label}
    </span>
  );
}

const categoryEmoji = (name = "") => {
  const s = String(name);
  if (s.includes("트랙터")) return "🚜";
  if (s.includes("콤바인")) return "🌾";
  if (s.includes("제어기") || s.includes("전자") || s.includes("전기")) return "⚡";
  if (s.includes("엔진") || s.includes("모터")) return "⚙️";
  if (s.includes("배터리")) return "🔋";
  if (s.includes("오일") || s.includes("연료")) return "🛢️";
  if (s.includes("이앙기") || s.includes("파종")) return "🌱";
  if (s.includes("바퀴") || s.includes("타이어")) return "⚫";
  return "🔧";
};

function Card({ children, padding = 24 }) {
  return (
    <section
      style={{
        background: "#fff",
        border: `1px solid ${C.line}`,
        borderRadius: 16,
        padding,
      }}
    >
      {children}
    </section>
  );
}

function CardTitle({ children, hint }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: 12,
        marginBottom: 14,
      }}
    >
      <h3
        style={{
          fontSize: 17,
          fontWeight: 700,
          color: C.ink,
          letterSpacing: "-0.01em",
          margin: 0,
        }}
      >
        {children}
      </h3>
      {hint && (
        <span style={{ fontSize: 12, color: C.sub, fontWeight: 500 }}>{hint}</span>
      )}
    </div>
  );
}

function HeroCard({ repair }) {
  const names = repair?.categoryListName ?? [];
  const primary = names[0] || "기타";
  const secondary = names.slice(1).filter((n) => n && n !== "기타");
  const emoji = categoryEmoji(primary);
  const created = repair?.createDate;
  const expert = !emptyCheck(repair?.expertName) ? repair.expertName : null;

  return (
    <Card>
      <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
        <div
          style={{
            width: 88,
            height: 88,
            borderRadius: 16,
            background: C.greenBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 42,
            flexShrink: 0,
          }}
        >
          {emoji}
        </div>
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
            <h1
              style={{
                margin: 0,
                fontSize: 24,
                fontWeight: 800,
                color: C.ink,
                letterSpacing: "-0.02em",
                lineHeight: 1.25,
              }}
            >
              {primary}
              {secondary.length > 0 && (
                <span style={{ color: C.sub, fontWeight: 500 }}> · {secondary.join(" · ")}</span>
              )}
            </h1>
            <StatusChip status={repair?.status} />
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              fontSize: 13,
              color: C.sub,
              flexWrap: "wrap",
            }}
          >
            <span>요청일 {formatDateTime(created)}</span>
            <span style={{ width: 2, height: 2, background: "#D1D5DB", borderRadius: 9999 }} />
            <span>{created ? moment(created).locale("ko").fromNow() : ""}</span>
            {expert && (
              <>
                <span style={{ width: 2, height: 2, background: "#D1D5DB", borderRadius: 9999 }} />
                <span>{maskMiddleCharacters(expert)} 기사 배정</span>
              </>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

function DescriptionCard({ etc }) {
  const has = !!etc && String(etc).trim().length > 0;
  return (
    <Card>
      <CardTitle hint={has ? `${etc.length}자` : undefined}>기타 내용</CardTitle>
      {has ? (
        <p
          style={{
            margin: 0,
            fontSize: 15,
            lineHeight: 1.65,
            color: C.body,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {etc}
        </p>
      ) : (
        <div style={{ fontSize: 14, color: C.sub }}>기타 내용이 없습니다.</div>
      )}
    </Card>
  );
}

function VoiceCard({ voiceUrl }) {
  if (!voiceUrl) return null;
  return (
    <Card>
      <CardTitle>음성 메모</CardTitle>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          padding: "12px 14px",
          background: C.greenBg,
          border: `1px solid ${C.greenBorder}`,
          borderRadius: 12,
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 9999,
            background: C.greenDark,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <FontAwesomeIcon icon={faMicrophone} />
        </div>
        <audio
          controls
          src={voiceUrl}
          preload="metadata"
          style={{ flex: 1, minWidth: 0, height: 40 }}
        >
          이 브라우저는 오디오 재생을 지원하지 않습니다.
        </audio>
      </div>
    </Card>
  );
}

function PhotosCard({ images = [] }) {
  return (
    <Card>
      <CardTitle hint={images.length > 0 ? `${images.length}장` : undefined}>
        첨부된 사진
      </CardTitle>
      {images.length > 0 ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
            gap: 12,
          }}
        >
          {images.map((url, i) => (
            <a
              href={url}
              data-fancybox="gallery"
              data-caption={`${i + 1}/${images.length}`}
              key={i}
              style={{
                display: "block",
                aspectRatio: "1 / 1",
                borderRadius: 12,
                overflow: "hidden",
                border: `1px solid ${C.line}`,
                background: "#F3F4F6",
              }}
            >
              <img
                src={url}
                alt={`첨부 ${i + 1}`}
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              />
            </a>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: 14, color: C.sub }}>첨부된 사진이 없습니다.</div>
      )}
    </Card>
  );
}

function ProgressCard({ status }) {
  const steps = [
    { key: "0", label: "요청 등록", icon: faClipboardCheck },
    { key: "1", label: "매칭 완료", icon: faHandshake },
    { key: "2", label: "수리 완료", icon: faFlagCheckered },
  ];
  const currentIdx = steps.findIndex((s) => s.key === String(status));
  const reached = (i) => currentIdx >= i;

  return (
    <Card>
      <CardTitle>진행 상황</CardTitle>
      <div style={{ position: "relative", padding: "8px 12px" }}>
        {/* 라인 */}
        <div
          style={{
            position: "absolute",
            top: 32,
            left: 12 + 24,
            right: 12 + 24,
            height: 3,
            background: C.line,
            borderRadius: 9999,
          }}
        />
        <div
          style={{
            position: "absolute",
            top: 32,
            left: 12 + 24,
            width: `calc(${(currentIdx / (steps.length - 1)) * 100}% - ${(currentIdx / (steps.length - 1)) * 48}px)`,
            height: 3,
            background: C.green,
            borderRadius: 9999,
            transition: "width 0.4s ease",
          }}
        />
        <div style={{ display: "flex", justifyContent: "space-between", position: "relative" }}>
          {steps.map((step, i) => {
            const ok = reached(i);
            return (
              <div
                key={step.key}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 8,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 9999,
                    background: ok ? C.greenDark : "#fff",
                    border: `2px solid ${ok ? C.greenDark : C.line}`,
                    color: ok ? "#fff" : C.sub,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s ease",
                    zIndex: 1,
                  }}
                >
                  <FontAwesomeIcon icon={step.icon} />
                </div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: ok ? 700 : 500,
                    color: ok ? C.ink : C.sub,
                  }}
                >
                  {step.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

// ==================== 신청자 목록 (농가 전용) ====================
// 이름 + 평점만 공개 — 전화번호 등 신상은 매칭 확정 전 비공개
function ApplicantsCard({ docId, onChanged }) {
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmTarget, setConfirmTarget] = useState(null); // 선택 확인 대상
  const [rejectTarget, setRejectTarget] = useState(null); // 거절 확인 대상
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [messageOpen, setMessageOpen] = useState(false);

  const load = async () => {
    try {
      const list = await getApplicantsApi(docId);
      setApplicants(list.filter((a) => String(a.status) === "0"));
    } catch {
      setApplicants([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId]);

  const handleSelect = async () => {
    if (!confirmTarget || busy) return;
    setBusy(true);
    try {
      const result = await selectApplyApi(docId, confirmTarget.docId);
      if (result?.success) {
        setMessage("매칭이 완료되었습니다.");
        setMessageOpen(true);
      } else if (result?.status === "point") {
        setMessage("해당 수리기사의 포인트가 부족해 매칭할 수 없습니다.\n다른 기사를 선택해주세요.");
        setMessageOpen(true);
        load();
      } else {
        setMessage(result?.message || "매칭 처리에 실패했습니다.");
        setMessageOpen(true);
      }
    } catch {
      setMessage("매칭 처리에 실패했습니다.");
      setMessageOpen(true);
    } finally {
      setBusy(false);
      setConfirmTarget(null);
    }
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    try {
      await rejectApplyApi(rejectTarget.docId);
      setApplicants((prev) => prev.filter((a) => a.docId !== rejectTarget.docId));
    } catch {}
    setRejectTarget(null);
  };

  const ratingText = (a) =>
    a.ratingCount > 0
      ? `★ ${Number(a.avgScore).toFixed(1)} (${a.ratingCount}건)`
      : "아직 평점 없음";

  return (
    <Card>
      <CardTitle hint={applicants.length > 0 ? `${applicants.length}명 신청` : undefined}>
        신청한 수리기사
      </CardTitle>
      {loading ? (
        <div style={{ fontSize: 14, color: C.sub }}>불러오는 중...</div>
      ) : applicants.length === 0 ? (
        <div
          style={{
            padding: "20px 16px",
            background: C.bg,
            borderRadius: 12,
            fontSize: 14,
            color: C.sub,
            textAlign: "center",
          }}
        >
          아직 신청한 수리기사가 없어요.
          <br />
          수리기사가 신청하면 여기에서 선택할 수 있습니다.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {applicants.map((a) => (
            <div
              key={a.docId}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "12px 14px",
                border: `1px solid ${C.line}`,
                borderRadius: 12,
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 9999,
                  background: C.greenBg,
                  color: C.greenDark,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 17,
                  fontWeight: 800,
                  flexShrink: 0,
                }}
              >
                {(a.expertName || "?").charAt(0)}
              </div>
              <div style={{ flex: 1, minWidth: 120 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>
                  {a.expertName || "수리기사"}
                </div>
                <div
                  style={{
                    marginTop: 2,
                    fontSize: 13,
                    color: a.ratingCount > 0 ? "#B45309" : C.sub,
                    fontWeight: 600,
                  }}
                >
                  {ratingText(a)}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={() => setConfirmTarget(a)}
                  style={{
                    height: 40,
                    padding: "0 18px",
                    borderRadius: 10,
                    border: "none",
                    background: C.greenDark,
                    color: "#fff",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  선택하기
                </button>
                <button
                  type="button"
                  onClick={() => setRejectTarget(a)}
                  style={{
                    height: 40,
                    padding: "0 14px",
                    borderRadius: 10,
                    border: `1px solid ${C.line}`,
                    background: "#fff",
                    color: C.sub,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  거절
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!confirmTarget}
        onClose={() => setConfirmTarget(null)}
        onConfirm={handleSelect}
        title={`${confirmTarget?.expertName || "이 수리기사"} 기사와 매칭하시겠습니까?`}
        message={"매칭이 확정되면 수리기사 연락처가 공유됩니다."}
      />
      <ConfirmDialog
        open={!!rejectTarget}
        onClose={() => setRejectTarget(null)}
        onConfirm={handleReject}
        title={`${rejectTarget?.expertName || "이 수리기사"} 기사의 신청을 거절하시겠습니까?`}
        message={"거절한 신청은 목록에서 사라집니다."}
      />
      <OnlyConfirmDialog
        open={messageOpen}
        onClose={() => {
          setMessageOpen(false);
          if (message.startsWith("매칭이 완료")) {
            onChanged ? onChanged() : window.location.reload();
          }
        }}
        message={message}
      />
    </Card>
  );
}

const MatchingDetailScreen = ({ isMobile }) => {
  const { docId } = useParams();
  const navigate = useNavigate();
  const { userData } = useUser();
  const [repair, setRepair] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notPageModal, setNotPageModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");

  useEffect(() => {
    setIsLoading(true);
    (async () => {
      const data = await getMatchingOne(docId);
      if (emptyCheck(data)) {
        setModalMessage("존재하지 않는 요청 입니다.");
        setNotPageModal(true);
        return;
      }
      // 등록 후 한 달 지난 매칭 대기 요청은 마감 처리 (수리기사가 옛 알림 등으로 진입한 경우)
      if (
        userData?.userType === "수리기사" &&
        String(data.status) === "0" &&
        data.createDate
      ) {
        const created = new Date(String(data.createDate).replace(" ", "T"));
        const monthAgo = new Date();
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        if (!Number.isNaN(created.getTime()) && created < monthAgo) {
          setModalMessage("마감된 요청입니다.");
          setNotPageModal(true);
          return;
        }
      }
      setRepair(data);
      setIsLoading(false);
    })();
  }, [docId, userData?.userType]);

  // Fancybox는 매 렌더마다 bind 호출되도 안전.
  Fancybox.bind("[data-fancybox]", {});

  const handleNotPage = () => {
    setNotPageModal(false);
    userData?.userType === "수리기사" ? navigate("/expertmain") : navigate("/main");
  };

  return (
    <>
      <div
        style={{
          fontFamily:
            'Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, sans-serif',
          background: C.bg,
          minHeight: "100vh",
          padding: "24px 20px 80px",
        }}
      >
        <div style={{ maxWidth: 900, margin: "0 auto", display: "flex", flexDirection: "column", gap: 16 }}>
          <button
            type="button"
            onClick={() => navigate(-1)}
            style={{
              alignSelf: "flex-start",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 14px",
              borderRadius: 9999,
              background: "#fff",
              border: `1px solid ${C.line}`,
              color: C.body,
              fontSize: 13,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
            <span>목록</span>
          </button>

          {isLoading || !repair ? (
            <div style={{ display: "flex", width: "100%", height: 500, alignItems: "center", justifyContent: "center" }}>
              <OnlyLoadingPage />
            </div>
          ) : (
            <>
              <HeroCard repair={repair} />
              <DescriptionCard etc={repair?.etc || ""} />
              <VoiceCard voiceUrl={repair?.voiceUrl} />
              <PhotosCard images={repair?.imageUrls || []} />
              <ProgressCard status={repair?.status} />

              {/* 농가 본인 요청 + 매칭 대기 → 신청자 목록에서 선택/거절 */}
              {userData?.id === repair?.id && String(repair?.status) === "0" && (
                <ApplicantsCard docId={docId} />
              )}

              {!isMobile && (
                <div
                  style={{
                    background: "#fff",
                    border: `1px solid ${C.line}`,
                    borderRadius: 16,
                    padding: 16,
                    position: "sticky",
                    bottom: 16,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
                  }}
                >
                  <DetailPageButton
                    userData={userData}
                    repair={repair}
                    docId={docId}
                    isMobile={isMobile}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {isMobile && repair && (
        <div
          style={{
            position: "sticky",
            bottom: 0,
            width: "100%",
            background: "#FFFFFF",
            padding: 16,
            borderTop: `1px solid ${C.line}`,
          }}
        >
          <DetailPageButton
            userData={userData}
            repair={repair}
            docId={docId}
            isMobile={isMobile}
          />
        </div>
      )}

      <OnlyConfirmDialog
        open={notPageModal}
        onClose={handleNotPage}
        message={modalMessage}
      />
    </>
  );
};

export default MatchingDetailScreen;
